import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'Toolip';
const SITE_URL = 'https://toolip.app';
const DEFAULT_TITLE = 'Toolip | Desktop Productivity Toolbelt for Windows';
const DEFAULT_DESCRIPTION =
  'Toolip is a customizable desktop productivity toolbelt for Windows with timers, notes, calculators, link organization, and workflow utilities.';
const DEFAULT_IMAGE = `${SITE_URL}/favicon.png`;

const routeSeo = {
  '/': {
    title: DEFAULT_TITLE,
    description:
      'Toolip is a customizable desktop productivity toolbelt for Windows that keeps timers, notes, calculators, and workflow utilities in one place.',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Toolip',
      applicationCategory: 'ProductivityApplication',
      operatingSystem: 'Windows',
      url: SITE_URL,
      description:
        'A customizable desktop productivity toolbelt for Windows with everyday workflow utilities.',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
    },
  },
  '/features': {
    title: 'Toolip Features | Desktop Productivity Widgets and Utilities',
    description:
      'Explore Toolip features including timers, note tools, link organization, calculations, themes, and other desktop workflow utilities.',
  },
  '/pricing': {
    title: 'Toolip Pricing | Free, Plus, and Teams Plans',
    description:
      'See Toolip pricing for free, Plus, and Teams plans. Compare desktop productivity features, premium tools, and team management options.',
  },
  '/get-started': {
    title: 'Download Toolip | Get Started on Windows',
    description:
      'Download the latest Windows build of Toolip and get started with a desktop productivity toolbelt built for fast, practical daily use.',
  },
  '/about': {
    title: 'About Toolip | Independent Productivity Software',
    description:
      'Learn about Toolip and the independent developer building the desktop productivity app, website, updates, and support experience.',
  },
  '/contact': {
    title: 'Contact Toolip | Support, Product, and Business Questions',
    description:
      'Contact Toolip directly for support, product questions, bug reports, feature ideas, pricing questions, and business inquiries.',
  },
  '/updates': {
    title: 'Toolip Updates | Release Notes and Product Changes',
    description:
      'Read Toolip release notes, version history, and product updates to see new tools, fixes, and ongoing product improvements.',
  },
  '/TeamInfo': {
    title: 'Toolip Teams | Team Billing, Seats, and Management',
    description:
      'Learn how Toolip Teams works, including team billing, seat management, invite flows, manager roles, and subscriptions.',
  },
  '/policies': {
    title: 'Toolip Privacy Policy and Terms of Service',
    description:
      'Review Toolip privacy policy and terms of service, including account data, billing, support, analytics, and acceptable use.',
  },
  '/community': {
    title: 'Toolip Community | Upcoming Community Widgets',
    description:
      'Preview the upcoming Toolip community area for shared widgets and in-app discovery.',
    robots: 'noindex,follow',
  },
  '/feedback': {
    title: 'Toolip Feedback',
    description: 'Signed-in users can share product feedback and improvement ideas for Toolip.',
    robots: 'noindex,nofollow',
  },
  '/LoginPage': {
    title: 'Sign In to Toolip',
    description: 'Sign in to your Toolip account to manage your workspace, subscription, and settings.',
    robots: 'noindex,nofollow',
  },
  '/register': {
    title: 'Create a Toolip Account',
    description: 'Create a Toolip account to access subscriptions, team features, and account settings.',
    robots: 'noindex,nofollow',
  },
  '/account': {
    title: 'Your Toolip Account',
    description: 'Manage your Toolip account, subscription, and team settings.',
    robots: 'noindex,nofollow',
  },
};

const ensureMetaByName = (name) => {
  let element = document.head.querySelector(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('name', name);
    document.head.appendChild(element);
  }
  return element;
};

const ensureMetaByProperty = (property) => {
  let element = document.head.querySelector(`meta[property="${property}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('property', property);
    document.head.appendChild(element);
  }
  return element;
};

const ensureCanonical = () => {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  return element;
};

const ensureStructuredData = () => {
  let element = document.head.querySelector('#toolip-structured-data');
  if (!element) {
    element = document.createElement('script');
    element.setAttribute('id', 'toolip-structured-data');
    element.setAttribute('type', 'application/ld+json');
    document.head.appendChild(element);
  }
  return element;
};

const SeoManager = () => {
  const location = useLocation();

  useEffect(() => {
    const metadata = routeSeo[location.pathname] || {};
    const title = metadata.title || DEFAULT_TITLE;
    const description = metadata.description || DEFAULT_DESCRIPTION;
    const robots = metadata.robots || 'index,follow';
    const canonicalUrl = `${SITE_URL}${location.pathname}`;

    document.title = title;
    ensureMetaByName('description').setAttribute('content', description);
    ensureMetaByName('robots').setAttribute('content', robots);
    ensureMetaByName('theme-color').setAttribute('content', '#111827');

    ensureMetaByProperty('og:type').setAttribute('content', 'website');
    ensureMetaByProperty('og:site_name').setAttribute('content', SITE_NAME);
    ensureMetaByProperty('og:title').setAttribute('content', title);
    ensureMetaByProperty('og:description').setAttribute('content', description);
    ensureMetaByProperty('og:url').setAttribute('content', canonicalUrl);
    ensureMetaByProperty('og:image').setAttribute('content', DEFAULT_IMAGE);

    ensureMetaByName('twitter:card').setAttribute('content', 'summary');
    ensureMetaByName('twitter:title').setAttribute('content', title);
    ensureMetaByName('twitter:description').setAttribute('content', description);
    ensureMetaByName('twitter:image').setAttribute('content', DEFAULT_IMAGE);

    ensureCanonical().setAttribute('href', canonicalUrl);

    const structuredData = ensureStructuredData();
    if (metadata.jsonLd) {
      structuredData.textContent = JSON.stringify(metadata.jsonLd);
    } else {
      structuredData.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: SITE_NAME,
        url: SITE_URL,
      });
    }
  }, [location.pathname]);

  return null;
};

export default SeoManager;
